import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import {
  Button,
  Container,
  Icon,
  InputCheckbox,
  InputToggle,
  Notifications,
  Text,
} from "@jds/core";

// Mui
import { DataGrid } from "@mui/x-data-grid";
import { Box, FormControlLabel, Switch } from "@mui/material";

// Local
import useApiHttp from "../../hooks/use-http";
import EmptyState from "../../components/NoData/EmptyState";
import SubSettingsHeader from "../../components/UI/SubSettingsHeader";
import { tableStyle } from "../../theme";
import Loader from "../../components/UI/Loader/Loader";
import JdsModal from "../../components/UI/JdsModal";


export default function PremiumAlbumsPage() {
  const { id: eventId } = useParams();
  const navigate = useNavigate();

  const [folderList, setFolderList] = useState([]);
  const [page, setPage] = useState(0);
  const [lastPage, setLastPage] = useState(0);
  const [isWatermarkAvailable, setIsWatermarkAvailable] = useState(false);
  const [isWatermarkAlertModalClose, setIsWatermarkAlertModalClose] =
    useState(true);

  const { sendRequest: getFoldersReq, isLoading: isFolGeting } = useApiHttp();
  const { sendRequest: folderPremiumReq, isLoading: isPremiumRequesting } =
    useApiHttp();
  const { sendRequest: getWatermarkReq, isLoading: waterReqGeting } =
    useApiHttp();

  // Watermark details
  const getWatermarkDetails = () => {
    getWatermarkReq(
      {
        url: `watermark/event/${eventId}`,
        base: "MEDIA",
      },
      null,
      (data) => {
        data.statusCode == 200
          ? setIsWatermarkAvailable(true)
          : setIsWatermarkAvailable(false);
      },
      (err, data) => {
        Notifications.toast.error()
      }
    );
  };

  const getFoldersHandler = () => {
    getFoldersReq(
      {
        url: `event-folders/get-event-folder/${eventId}?page=${page}&size=500`,
      },
      null,
      (data) => {
        if (page == 0) {
          setFolderList(data.data.results);
          setLastPage(data?.data.lastPage);
        } else {
          setFolderList((pre) => [...pre, ...data.data.results]);
          setLastPage(data?.data.lastPage);
        }
      },
      (err, data) => {
        Notifications.toast.error({
          id: "get-folder",
          title: data?.message,
        });
      }
    );
  };

  const togglePremiumStatus = (id, isPremium) => {
    setFolderList((prevFolders) =>
      prevFolders.map(
        (folder) =>
          folder.id === id
            ? { ...folder, isPremium: isPremium } // Toggle isPremium
            : folder // Return the folder unchanged
      )
    );
  };

  const folderPremiumHandler = (id, isPremium) => {
    folderPremiumReq(
      {
        url: "event-folders/switch-premium",
        method: "PATCH",
        body: {
          id: id,
          isPremium: isPremium,
        },
      },
      (data) => { },
      (data) => {
        togglePremiumStatus(id, isPremium);

        Notifications.toast.success({
          id: "premium-hadle",
          title: data?.message,
        });
      }
    );
  };

  const observerRef = useRef(null);
  const lastElementRef = useCallback(
    (node) => {
      if (isFolGeting) return;
      if (observerRef.current) observerRef.current.disconnect();
      observerRef.current = new IntersectionObserver((element) => {
        if (element[0].isIntersecting && lastPage > page) {
          setPage((pre) => pre + 1);
        }
      });
      if (node) observerRef.current.observe(node);
    },
    [isFolGeting, lastPage]
  );

  const columns = [
    { field: "s_no", headerName: "S.No", width: 100, editable: false },
    {
      field: "name",
      headerName: "Album Name",
      width: 300,
      editable: false,
    },
    {
      field: "status",
      headerName: "Status",
      width: 250,
      editable: false,
      sortable: false,
      renderCell: (val) => {
        return (
          <Icon
            ic={"IcCrowns"}
            color={val.row?.isPremium ? "feedback_warning_50" : "grey-60"}
          />
        );
      },
    },

    {
      field: "action",
      headerName: "Action",
      width: 250,
      editable: false,
      sortable: false,
      renderCell: (val) => {
        const isPremium = val.row?.isPremium
        return (
          // <InputToggle
          //   type="toggle"
          //   label=""
          //   name="premium"
          //   size="medium"
          //   checked={isPremium}
          //   onChange={(e) => {
          //     const isChecked = e.target.checked;
          //     if (isWatermarkAvailable) {
          //       folderPremiumHandler(val.row?.id, isChecked);
          //       console.log('watermark available')
          //     } else {
          //       console.log('watermark not available')
          //       if (!isPremium) {
          //         setIsWatermarkAlertModalClose(false);
          //       } else {
          //         folderPremiumHandler(val.row?.id, isChecked);
          //       }
          //     }

          //   }}
          // />

          <FormControlLabel
            checked={!isPremium && !isWatermarkAvailable ? false : isPremium}
            control={<Switch />}
            label={<Text text="Premium" color="primary_grey_80" />}
            labelPlacement="start"
            onChange={(e) => {
              const isChecked = e.target.checked;
              if (isWatermarkAvailable) {
                folderPremiumHandler(val.row?.id, isChecked);
              } else {
                if (!isPremium) {
                  setIsWatermarkAlertModalClose(false);
                } else {
                  folderPremiumHandler(val.row?.id, isChecked);
                }
              }
            }}
            sx={{ ml: 0, justifyContent: "flex-end" }}
          />
        );
      },
    },
  ];

  useEffect(() => {
    getFoldersHandler();
  }, [page]);

  useEffect(() => {
    getWatermarkDetails();
  }, []);
  return (
    <div className="premium-albums" style={{ marginBottom: "24px" }}>
      <SubSettingsHeader heading="Premium Albums" />

      {isFolGeting ? <Loader size="medium" /> : folderList.length > 0 ? (
        <Box mt={4}>
          <DataGrid
            rows={folderList.map((val, i) => ({ ...val, s_no: i + 1 }))}
            columns={columns}
            initialState={{
              pagination: {
                paginationModel: {
                  pageSize: 5,
                },
              },
            }}
            pageSizeOptions={[10, 15]}
            disableColumnMenu
            disableRowSelectionOnClick
            loading={isPremiumRequesting}
            sx={tableStyle}
          />
        </Box>
      ) : (
        <EmptyState
          title="No albums found!"
          subtitle="Create albums to make them premium."
          action={false}
          mt={3}
        />
      )}

      <JdsModal
        size="m"
        kind="informational"
        close={isWatermarkAlertModalClose}
        onClose={() => {
          setIsWatermarkAlertModalClose(true);
        }}
      >
        <Box>
          <EmptyState
            title="Watermark not found for this Event!"
            subtitle="Please create watermark to enable premium feature."
            action={false}
          />
          <Container fullWidth horizontalAlign="center" style={{ marginTop: '16px' }}>
            <Button label="Create Watermark" icon={'IcArrowNext'} onClick={() => navigate(`/home/events/event-detail/${eventId}`, { state: { isWatermarkModalOpen: true } })} />
          </Container>
        </Box>
      </JdsModal>
    </div>
  );
}
